import {
  ContactFormFields,
  HubspotConvertingFormTypeOptions,
} from '@transcend-io/contact-form-schema';
import { TrackingEvent } from '@transcend-io/segment/core';
import { groq } from 'next-sanity';
import { z } from 'zod';

import { basicText, SanityBasicText } from './basicText';
import type { SanityFormConfirmation } from './formConfirmation';

export const SanityHubspotConvertingFormType = z
  .enum(HubspotConvertingFormTypeOptions)
  .nullish();

const SanityFormFieldBase = z.object({
  _key: z.string(),
  label: z.string(),
  isRequired: z.boolean(),
});

export const SanityFormTrackingEventProperties = z
  .object({
    contentTitle: z.string().optional(),
  })
  .optional()
  .nullable();

export const SanityFormFieldCountryRegion = SanityFormFieldBase.extend({
  _type: z.literal('countryRegion'),
});
export type SanityFormFieldCountryRegion = z.infer<
  typeof SanityFormFieldCountryRegion
>;

export const SanityFormFieldEmail = SanityFormFieldBase.extend({
  _type: z.literal('email'),
  name: z.literal('email'),
  requireBusinessEmail: z.boolean().optional().nullable(),
});
export type SanityFormFieldEmail = z.infer<typeof SanityFormFieldEmail>;

export const SanityFormSelectField = SanityFormFieldBase.extend({
  _type: z.literal('select'),
  name: ContactFormFields.keyof(),
});
export type SanityFormSelectField = z.infer<typeof SanityFormSelectField>;

export const SanityFormFieldText = SanityFormFieldBase.extend({
  _type: z.literal('text'),
  name: ContactFormFields.keyof(),
  isHalfWidthOnDesktop: z.boolean().optional().nullable(),
});
export type SanityFormFieldText = z.infer<typeof SanityFormFieldText>;

export const SanityFormFieldTextarea = SanityFormFieldBase.extend({
  _type: z.literal('textarea'),
  name: ContactFormFields.keyof(),
  isHalfWidthOnDesktop: z.boolean().optional().nullable(),
});
export type SanityFormFieldTextarea = z.infer<typeof SanityFormFieldTextarea>;

export const SanityFormFieldPhone = SanityFormFieldBase.extend({
  _type: z.literal('tel'),
  name: z.literal('phone'),
});
export type SanityFormFieldPhone = z.infer<typeof SanityFormFieldPhone>;

export const SanityFormField = z.union([
  SanityFormFieldCountryRegion,
  SanityFormFieldEmail,
  SanityFormSelectField,
  SanityFormFieldText,
  SanityFormFieldTextarea,
  SanityFormFieldPhone,
]);
export type SanityFormField = z.infer<typeof SanityFormField>;

export const SanityFormFieldGroup = z.object({
  title: z.string().optional().nullable(),
  fields: z.array(SanityFormField),
});
export type SanityFormFieldGroup = z.infer<typeof SanityFormFieldGroup>;

const SanityFormCommon = z.object({
  _id: z.string().optional(),
  formTitle: z.string(),
  trackingEventName: TrackingEvent,
  trackingEventProperties: SanityFormTrackingEventProperties,
  hubspotCampaignId: z.string().uuid().nullish(),
  hubspotConvertingFormType: SanityHubspotConvertingFormType,
  contentPieceTitle: z.string().nullish(),
  hubspotEmailSubscriptionIds: z.array(z.string()).nullish(),
  chiliPiperRouterId: z.string().nullish(),
  submitButtonLabel: z.string(),
  legalText: SanityBasicText.optional().nullable(),
});

const SanityDefaultForm = SanityFormCommon.extend({
  formType: z.literal('default'),
  fields: z.array(SanityFormField),
});

const SanityMultiForm = SanityFormCommon.extend({
  formType: z.literal('multistep'),
  fieldGroups: z.array(SanityFormFieldGroup),
});

const SanityEnrichedForm = SanityFormCommon.extend({
  formType: z.literal('enriched'),
  fieldGroups: z.array(SanityFormFieldGroup),
  strictEnrichment: z.boolean(),
});

const SanityContactForm = SanityFormCommon.extend({
  formType: z.literal('contact'),
});

export const SanityForm = z.discriminatedUnion('formType', [
  SanityDefaultForm,
  SanityMultiForm,
  SanityEnrichedForm,
  SanityContactForm,
]);

export type SanityFormSelect = z.infer<typeof SanityForm>;
export type SanityMultiForm = z.infer<typeof SanityMultiForm>;
export type SanityEnrichedForm = z.infer<typeof SanityEnrichedForm>;
export type SanityContactForm = z.infer<typeof SanityContactForm>;
export type SanityDefaultForm = z.infer<typeof SanityDefaultForm>;

export type FormProps<T> = {
  form: T;
  confirmation: SanityFormConfirmation | string;
  onSubmitSuccess?: () => void;
  onSubmitError?: () => void;
  className?: string;
  theme?: 'light' | 'dark';
  /** Confirmation CTA surface theme; defaults to `theme` when omitted. */
  buttonTheme?: 'light' | 'dark';
};

const formFieldsProjectionGroq = groq`{
  _key,
  label,
  isRequired,
  _type == 'formFieldText' => {
    name,
    isHalfWidthOnDesktop,
    '_type': 'text',
  },
  _type == 'formFieldEmail' => {
    name,
    requireBusinessEmail,
    '_type': 'email',
  },
  _type == 'formFieldPhone' => {
    name,
    '_type': 'tel',
  },
  _type == 'formFieldTextarea' => {
    name,
    '_type': 'textarea',
  },
  _type == 'formFieldSelect' => {
    name,
    '_type': 'select',
  },
  _type == 'formFieldCountryRegion' => {
    '_type': 'countryRegion',
  },
}`;

export const form = groq`{
  _id,
  'formType': coalesce(formType, 'default'),
  "formTitle": title,
  trackingEventName,
  trackingEventProperties,
  hubspotCampaignId,
  hubspotConvertingFormType,
  contentPieceTitle,
  hubspotEmailSubscriptionIds,
  chiliPiperRouterId,
  ...select(
    coalesce(formType, 'default') == 'default' => {
      'fields': fields[] ${formFieldsProjectionGroq}
    },
    coalesce(formType, 'default') in ['multistep', 'enriched'] => {
      'fieldGroups': fieldGroups[] {
      _key,
        title,
        'fields': fields[] ${formFieldsProjectionGroq}
      },
      strictEnrichment,
    },
    true => {}
  ),
  submitButtonLabel,
  legalText[] ${basicText},
}`;
